import React, { useState, useEffect, useCallback } from 'react';
import { useModalAlert } from './ModalAlert';
import { portfolioFetch } from '../utils/portfolioFetch';
import {
  FaSave,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaCoins,
  FaSearch,
  FaExclamationTriangle,
  FaCheckCircle,
  FaLock
} from 'react-icons/fa';

/**
 * PortfolioTokenSelector - Component for selecting which tokens to trade in a portfolio
 * 
 * Fetches available tokens from the API and allows users to select/deselect tokens.
 * Selected tokens are saved via PUT /portfolio/{portfolio_id}
 */
// Helper function to get usage indicator based on percentage
const getUsageIndicator = (usage, limit) => {
  if (limit === 0) {
    return {
      icon: FaTimes,
      color: "text-red-400",
      bgColor: "bg-red-400/10",
      borderColor: "border-red-400/30",
      status: "disabled"
    };
  }
  
  const percentage = (usage / limit) * 100;
  
  if (percentage >= 100) {
    return {
      icon: FaLock,
      color: "text-red-400",
      bgColor: "bg-red-400/10",
      borderColor: "border-red-400/30",
      status: "limit_reached"
    };
  } else if (percentage >= 90) {
    return {
      icon: FaExclamationTriangle,
      color: "text-red-400",
      bgColor: "bg-red-400/10",
      borderColor: "border-red-400/30",
      status: "critical"
    };
  } else if (percentage >= 70) {
    return {
      icon: FaExclamationTriangle,
      color: "text-yellow-400",
      bgColor: "bg-yellow-400/10",
      borderColor: "border-yellow-400/30",
      status: "warning"
    };
  } else {
    return {
      icon: FaCheckCircle,
      color: "text-green-400",
      bgColor: "bg-green-400/10",
      borderColor: "border-green-400/30",
      status: "good"
    };
  }
};

// Helper function to get feature usage from userData
const getFeatureUsage = (userData, featureType) => {
  if (!userData?.feature_usage) return null;
  return userData.feature_usage.find(usage => usage.feature_type === featureType);
};

const PortfolioTokenSelector = ({ 
  portfolioId, 
  config,
  authToken,
  initialSelectedTokens = [],
  onTokensChange,
  userData = null,
  className = "" 
}) => {
  // State management
  const [availableTokens, setAvailableTokens] = useState([]);
  const [selectedTokens, setSelectedTokens] = useState(new Set(initialSelectedTokens));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  
  const { showSuccess, showError } = useModalAlert();
  
  // Get token limit from user's plan
  const tokenUsage = getFeatureUsage(userData, 'TOKEN_COUNT');
  const maxTokens = tokenUsage?.limit_count ?? userData?.plan?.number_tokens ?? Infinity;
  const currentTokenCount = selectedTokens.size;
  const isAtLimit = currentTokenCount >= maxTokens;

  // API base URL
  const apiUrl = config?.apiBaseUrl || 'http://localhost:8000';

  // Fetch available tokens for the portfolio
  const fetchAvailableTokens = useCallback(async () => {
    if (!portfolioId || !authToken) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await portfolioFetch(`${apiUrl}/portfolio/${portfolioId}/tokens`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }, portfolioId);

      if (!response.ok) {
        throw new Error(`Failed to fetch tokens: ${response.statusText}`);
      }

      const tokens = await response.json();
      setAvailableTokens(tokens || []);
    } catch (err) {
      console.error('Error fetching tokens:', err);
      setError(err.message);
      showError('Failed to load available tokens');
    } finally {
      setIsLoading(false);
    }
  }, [portfolioId, authToken, apiUrl, showError]);

  // Load tokens on mount and when portfolioId changes
  useEffect(() => {
    fetchAvailableTokens();
  }, [fetchAvailableTokens]);

  // Update selected tokens when initialSelectedTokens prop changes
  useEffect(() => {
    setSelectedTokens(new Set(initialSelectedTokens));
    setHasChanges(false);
  }, [initialSelectedTokens]);

  // Toggle token selection
  const toggleToken = (token) => {
    setSelectedTokens(prev => {
      const newSet = new Set(prev);
      if (newSet.has(token)) {
        newSet.delete(token);
      } else {
        // Check if we're at the limit before adding
        if (newSet.size >= maxTokens) {
          showError(`Token limit reached. Your plan allows ${maxTokens} tokens. Upgrade to select more.`);
          return prev;
        }
        newSet.add(token);
      }
      setHasChanges(true);
      return newSet;
    });
  };

  // Select all tokens (respecting limit)
  const selectAll = () => {
    const tokensToSelect = filteredTokens.slice(0, maxTokens);
    if (filteredTokens.length > maxTokens) {
      showError(`Your plan allows ${maxTokens} tokens. Only selecting first ${maxTokens}.`);
    }
    setSelectedTokens(new Set(tokensToSelect));
    setHasChanges(true);
  };

  // Deselect all tokens
  const deselectAll = () => {
    setSelectedTokens(new Set());
    setHasChanges(true);
  };

  // Save selected tokens to the portfolio
  const saveTokenSelection = async () => {
    if (!portfolioId || !authToken) {
      showError('Unable to save: missing portfolio or authentication');
      return;
    }

    // Prevent saving with no tokens selected
    if (selectedTokens.size === 0) {
      showError('You must select at least one token. Recommendations require at least one token to analyze.');
      return;
    }

    setIsSaving(true);

    try {
      const response = await portfolioFetch(`${apiUrl}/portfolio/${portfolioId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tokens_selected: Array.from(selectedTokens)
        }),
      }, portfolioId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to save: ${response.statusText}`);
      }

      const updatedPortfolio = await response.json();
      setHasChanges(false);
      showSuccess(`Saved ${selectedTokens.size} token(s) to portfolio`);
      
      // Notify parent component of the change
      if (onTokensChange) {
        onTokensChange(Array.from(selectedTokens));
      }
    } catch (err) {
      console.error('Error saving token selection:', err);
      showError(err.message || 'Failed to save token selection');
    } finally {
      setIsSaving(false);
    }
  };

  // Filter tokens by search query
  const filteredTokens = availableTokens.filter(token =>
    token.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render loading state
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <FaSpinner className="animate-spin text-2xl text-cyan-500 mr-3" />
        <span className="text-gray-400">Loading available tokens...</span>
      </div>
    );
  }

  // Render error state
  if (error && availableTokens.length === 0) {
    return (
      <div className={`p-1 sm:p-4 bg-red-500/10 border border-red-500/30 rounded ${className}`}>
        <p className="text-red-400 text-sm">{error}</p>
        <button
          onClick={fetchAvailableTokens}
          className="mt-1 sm:mt-2 px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm bg-red-500/20 hover:bg-red-500/30 rounded text-red-300 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-white/5 backdrop-blur rounded border border-white/10 ${className}`}>
      {/* Header */}
      <div className="p-1 sm:p-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <FaCoins className="text-cyan-400 text-sm sm:text-base" />
            <h3 className="text-sm sm:text-xlg font-semibold text-white">Tokens</h3>
            
            {/* Token usage indicator */}
            {maxTokens !== Infinity && (() => {
              const indicator = getUsageIndicator(currentTokenCount, maxTokens);
              const IconComponent = indicator.icon;
              const percentage = maxTokens > 0 ? Math.round((currentTokenCount / maxTokens) * 100) : 0;
              return (
                <div className={`flex items-center gap-1 sm:gap-2 px-1 sm:px-3 py-0.5 sm:py-1 rounded ${indicator.bgColor} ${indicator.borderColor} border`}>
                  <IconComponent className={`h-3 w-3 sm:h-4 sm:w-4 ${indicator.color}`} />
                  <span className={`text-xs sm:text-sm font-medium ${indicator.color}`}>
                    {currentTokenCount}/{maxTokens}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${indicator.bgColor} ${indicator.borderColor} border`}>
                    {percentage}%
                  </span>
                </div>
              );
            })()}
            
            {/* Fallback display when no limit info */}
            {maxTokens === Infinity && (
              <span className="text-sm text-gray-400">
                ({selectedTokens.size} of {availableTokens.length} selected)
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            {hasChanges && selectedTokens.size === 0 && (
              <span className="text-xs text-red-400 mr-1 sm:mr-2">⚠️ Min 1</span>
            )}
            {hasChanges && selectedTokens.size > 0 && (
              <span className="text-xs text-amber-400 mr-1 sm:mr-2">Unsaved</span>
            )}
            <button
              onClick={saveTokenSelection}
              disabled={isSaving || !hasChanges || selectedTokens.size === 0}
              className={`
                flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all
                ${hasChanges && selectedTokens.size > 0
                  ? 'bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 text-white shadow-lg shadow-cyan-500/25' 
                  : 'bg-white/10 text-gray-500 cursor-not-allowed'}
              `}
            >
              {isSaving ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Selection
                </>
              )}
            </button>
          </div>
        </div>

        {/* Search and bulk actions */}
        <div className="flex items-center gap-1 sm:gap-4">
          <div className="relative flex-1">
            <FaSearch className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs sm:text-sm" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-6 sm:pl-10 pr-2 sm:pr-4 py-1 sm:py-2 text-sm bg-white/5 border border-white/10 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
            />
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={selectAll}
              className="px-1.5 sm:px-3 py-1 sm:py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded text-cyan-400 transition-colors"
            >
              All
            </button>
            <button
              onClick={deselectAll}
              className="px-1.5 sm:px-3 py-1 sm:py-2 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded text-gray-400 transition-colors"
            >
              None
            </button>
          </div>
        </div>
      </div>

      {/* Token grid */}
      <div className="p-1 sm:p-4 max-h-96 overflow-y-auto">
        {filteredTokens.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchQuery ? 'No tokens match your search' : 'No tokens available'}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-1 sm:gap-2">
            {filteredTokens.map((token) => {
              const isSelected = selectedTokens.has(token);
              return (
                <button
                  key={token}
                  onClick={() => toggleToken(token)}
                  className={`
                    flex items-center justify-between gap-1 px-1 sm:px-3 py-1 sm:py-2 rounded border transition-all text-xs sm:text-sm
                    ${isSelected 
                      ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' 
                      : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <span className="font-medium truncate">{token}</span>
                  {isSelected ? (
                    <FaCheck className="text-cyan-400 flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded border border-white/20 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with selected tokens summary */}
      {selectedTokens.size > 0 && (
        <div className="p-1 sm:p-4 border-t border-white/10 bg-white/5">
          <div className="flex flex-wrap gap-1 sm:gap-2">
            <span className="text-xs text-gray-400 mr-1 sm:mr-2">Sel:</span>
            {Array.from(selectedTokens).slice(0, 8).map((token) => (
              <span
                key={token}
                className="inline-flex items-center gap-0.5 px-1 sm:px-2 py-0.5 sm:py-1 bg-cyan-500/20 rounded text-xs text-cyan-300"
              >
                {token}
                <button
                  onClick={() => toggleToken(token)}
                  className="hover:text-red-400 transition-colors"
                >
                  <FaTimes className="text-xs" />
                </button>
              </span>
            ))}
            {selectedTokens.size > 10 && (
              <span className="text-xs text-gray-500">
                +{selectedTokens.size - 10} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioTokenSelector;
